import { getCmSites, getProductionMonitor } from '@api/cm-sites';
import { ArrowLeft } from '@phosphor-icons/react';

import PageHeader from '@components/common/PageHeader';
import styles from './ProductionMonitor.module.css';
import { GenericChartCard } from '@components/ui/Chart';
import { DateSinglePicker } from '@components/ui/DateRange';
import { DataSheet, type DataSheetColumn } from '@components/ui/DataSheet';
import StatCard from '@components/ui/StatCard';
import { Box, Button, Group, SegmentedControl, Select, Text } from '@mantine/core';
import { ButtonVariant } from '@providers/Mantine/mantineTheme';
import { neutralColors, semanticColors } from '@styles/design-system';
import { useQuery } from '@tanstack/react-query';
import {
    formatDayLabel,
    startOfWindow,
    today as localToday,
} from '@utils/productionWindow';
import { useMemo, useState, type FC } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { formatCount } from '@utils/currency';

/**
 * Thirty days is four weekly cycles, which is what makes a rhythm legible — a fortnight shows
 * two, too few to tell a slow week from a slow month. Fifteen is offered because a narrow
 * screen reads it better.
 */
const WINDOW_OPTIONS = [
    { label: '15 days', value: '15' },
    { label: '30 days', value: '30' },
];

/** What a row of the breakdown is, for the count line above it. */
const rowNoun = (groupBy: 'product' | 'warehouse' | 'batch', count: number): string => {
    const singular = { product: 'product', warehouse: 'vendor', batch: 'batch' }[groupBy];
    if (count === 1) return singular;

    return singular === 'batch' ? 'batches' : `${singular}s`;
};

interface TrendRow {
    day: string;
    label: string;
    quantity: number;
    runs: number;
}

/**
 * What the brand's manufacturers made, per day.
 *
 * ONE SCREEN, TWO SCOPES. Opened from CM Sites it answers across every manufacturer; opened
 * from a site it answers for that one, and the scope control moves between them without going
 * back. The alternative — an aggregate screen and a per-site panel — is two things to keep in
 * step, and the day they drift the brand has two different answers to one question.
 *
 * THE CHART IS THE DATE PICKER. Clicking a bar re-scopes the headline and the list to that day.
 * A screen that already draws thirty days does not need a separate control to choose among
 * them, and the day lives in the URL so "look at the 4th" is a link.
 */
const ProductionMonitorContainer: FC = () => {
    const navigate = useNavigate();
    const [params, setParams] = useSearchParams();

    const warehouseId = params.get('site') ?? undefined;
    const explicitDay = params.get('day');
    const day = explicitDay ?? localToday();
    const [windowDays, setWindowDays] = useState('30');
    type GroupBy = 'product' | 'warehouse' | 'batch';
    const [groupBy, setGroupBy] = useState<GroupBy>(
        // Narrowing to one site is what somebody does to see the runs themselves.
        warehouseId ? 'batch' : 'warehouse',
    );

    /**
     * The window ends on the day being looked at, not on today.
     *
     * Anchoring it to today would mean picking a date from last month puts its bar outside the
     * chart — the figure would change and the graph beside it would not, which is the sort of
     * thing that makes somebody stop trusting a screen.
     */
    const anchorDay = explicitDay ?? localToday();
    const fromDate = useMemo(
        () => startOfWindow(anchorDay, Number(windowDays)),
        [anchorDay, windowDays],
    );
    const toDate = anchorDay;

    const { data: sitesData } = useQuery(['cm-sites', 'monitor-scope'], () =>
        getCmSites(),
    );

    const { data, isLoading } = useQuery(
        ['production-monitor', warehouseId ?? 'all', day, fromDate, toDate, groupBy, !explicitDay],
        () =>
            getProductionMonitor({
                day,
                fromDate,
                toDate,
                warehouseId,
                groupBy,
                // Only when nobody picked a day. Clicking a bar means that bar, even an empty one.
                fallbackToLastRun: !explicitDay,
            }),
        { keepPreviousData: true },
    );

    const setParam = (key: string, value?: string) => {
        const next = new URLSearchParams(params);
        if (value) next.set(key, value);
        else next.delete(key);
        setParams(next);
    };

    const trend: TrendRow[] = useMemo(
        () =>
            (data?.trend ?? []).map((point) => ({
                day: point.day,
                label: formatDayLabel(point.day),
                quantity: point.quantity,
                runs: point.runs,
            })),
        [data],
    );

    // The day the answer is ABOUT, which is not always the day that was asked for.
    const shownDay = data?.selectedDay ?? day;
    const selectedIndex = trend.findIndex((row) => row.day === shownDay);

    const siteOptions = useMemo(
        () => [
            { value: '', label: 'All vendors' },
            ...(sitesData?.sites ?? []).map((site) => ({
                value: site.warehouseId,
                label: site.vendorName
                    ? `${site.vendorName} · ${site.name}`
                    : site.name,
            })),
        ],
        [sitesData],
    );

    const columns = useMemo<DataSheetColumn<Record<string, unknown>>[]>(() => {
        const units: DataSheetColumn<Record<string, unknown>> = {
            key: 'quantity',
            header: 'Units',
            width: 110,
            align: 'right',
            sortable: true,
        };

        if (groupBy === 'batch') {
            return [
                { key: 'label', header: 'Batch', width: 200 },
                { key: 'productName', header: 'Product', width: 280 },
                ...(warehouseId
                    ? []
                    : [{ key: 'siteName', header: 'Vendor', width: 220 }]),
                units,
                {
                    key: 'qcStatus',
                    header: 'QC',
                    width: 130,
                    filterable: true,
                    render: (value: unknown) => {
                        const status = (value as string) ?? null;
                        if (!status) return <Text fz={13} c={neutralColors.textMuted}>—</Text>;

                        // Quarantine-and-release: PENDING is not a warning, it is the normal
                        // state of a batch that has just been made and is waiting on the brand.
                        const tone =
                            status === 'PASSED'
                                ? semanticColors.positive.dark
                                : status === 'FAILED'
                                  ? semanticColors.critical.primary
                                  : semanticColors.warning.dark;
                        const label =
                            status === 'PASSED'
                                ? 'Released'
                                : status === 'FAILED'
                                  ? 'Failed'
                                  : 'QC pending';

                        return <Text fz={13} c={tone}>{label}</Text>;
                    },
                },
                {
                    key: 'expiryDate',
                    header: 'Expires',
                    width: 130,
                    render: (value: unknown) =>
                        value ? (
                            <Text fz={13}>{formatDayLabel(value as string)}</Text>
                        ) : (
                            <Text fz={13} c={neutralColors.textMuted}>—</Text>
                        ),
                },
            ];
        }

        return [
            {
                key: 'label',
                header: groupBy === 'product' ? 'Product' : 'Vendor',
                width: 320,
            },
            units,
            { key: 'runs', header: 'Runs', width: 90, align: 'right' },
            ...(groupBy === 'warehouse'
                ? [{ key: 'skus', header: 'SKUs', width: 90, align: 'right' as const }]
                : []),
        ];
    }, [groupBy, warehouseId]);

    const isToday = shownDay === localToday();
    const delta =
        data && data.averageDaily > 0
            ? Math.round(
                  ((data.selectedQuantity - data.averageDaily) / data.averageDaily) * 100,
              )
            : null;

    // Not h-full: with a fixed page height the grid gets whatever is left after the chart
    // and scrolls one row at a time inside its own box. Letting the column grow and the page
    // scroll means the table shows its rows.
    return (
        <div className="flex w-full flex-col gap-4 page-shell">
            <PageHeader
                className={styles.header}
                icon="🏭"
                title="Monitor production"
                subtitle={
                    data?.fellBackToLastRun
                        ? `Nothing recorded today yet — showing ${formatDayLabel(shownDay)}, the last day that ran`
                        : isToday
                          ? 'What your manufacturers made today'
                          : `What your manufacturers made on ${formatDayLabel(shownDay)}`
                }
                actions={
                    <Group gap={8}>
                        {/*
                          * Picking the day directly, rather than only by clicking a bar. A
                          * chart is a fine way to move between recent days and a poor way to
                          * reach a specific one — nobody hunts for the 4th among thirty bars.
                          *
                          * Clearing it hands the day back to the screen, which returns to the
                          * most recent day that ran.
                          */}
                        <Box w={150}>
                            <DateSinglePicker
                                value={shownDay}
                                onChange={(next) => setParam('day', next || undefined)}
                                placeholder="Pick a day"
                                clearable
                            />
                        </Box>
                        <Select
                            data={siteOptions}
                            value={warehouseId ?? ''}
                            onChange={(next) => setParam('site', next || undefined)}
                            allowDeselect={false}
                            w={260}
                            aria-label="Which manufacturers to show"
                        />
                        <Button
                            size="xs"
                            variant={ButtonVariant.DEFAULT}
                            leftSection={<ArrowLeft size={14} />}
                            onClick={() => navigate('/cm-sites')}
                        >
                            All sites
                        </Button>
                    </Group>
                }
            />

            <Group gap={16} grow align="stretch">
                <StatCard
                    label={isToday ? 'Produced today' : `Produced ${formatDayLabel(shownDay)}`}
                    value={formatCount(data?.selectedQuantity) ?? '—'}
                    helper={
                        delta === null
                            ? 'No comparable history yet'
                            : `${delta >= 0 ? '+' : ''}${delta}% on the ${windowDays}-day average`
                    }
                    valueColor={
                        delta === null || delta >= 0
                            ? semanticColors.positive.dark
                            : semanticColors.warning.dark
                    }
                    loading={isLoading}
                />
                <StatCard
                    label="Runs declared"
                    value={data?.selectedRuns ?? '—'}
                    helper="Undone runs are not counted"
                    valueColor={neutralColors.textPrimary}
                    loading={isLoading}
                />
                <StatCard
                    label={`${windowDays}-day average`}
                    value={formatCount(data?.averageDaily) ?? '—'}
                    helper="Across days that ran, not calendar days"
                    valueColor={neutralColors.textPrimary}
                    loading={isLoading}
                />
            </Group>

            <GenericChartCard<TrendRow>
                kind="bar"
                title="Daily output"
                subtitle="Click a bar to move the whole screen to that day"
                data={trend}
                dimensionKey="label"
                dimensionType="category"
                series={[{ id: 'quantity', key: 'quantity', label: 'Units produced' }]}
                height={180}
                loading={isLoading}
                onPointClick={(row) => setParam('day', row.day)}
                selectedIndex={selectedIndex === -1 ? null : selectedIndex}
                rightSlot={
                    <SegmentedControl
                        size="xs"
                        data={WINDOW_OPTIONS}
                        value={windowDays}
                        onChange={setWindowDays}
                    />
                }
            />

            <DataSheet<Record<string, unknown>>
                data={(data?.breakdown ?? []) as unknown as Record<string, unknown>[]}
                columns={columns}
                title="What ran"
                subtitle={
                    isLoading
                        ? 'Loading…'
                        : `${data?.breakdown.length ?? 0} ${rowNoun(
                              groupBy,
                              data?.breakdown.length ?? 0,
                          )} on this day`
                }
                isLoading={isLoading}
                // Fit the content and let the page scroll, as the sales tables do.
                maxHeight="auto"
                enableSearch
                searchPlaceholder={
                    {
                        product: 'Search product…',
                        warehouse: 'Search vendor…',
                        batch: 'Search batch…',
                    }[groupBy]
                }
                enableDownload
                toolbarControls={
                    <SegmentedControl
                        size="xs"
                        data={[
                            // Only offered across manufacturers — with one site in view there
                            // is no site axis to group on.
                            ...(warehouseId
                                ? []
                                : [{ label: 'By vendor', value: 'warehouse' }]),
                            { label: 'By product', value: 'product' },
                            { label: 'By batch', value: 'batch' },
                        ]}
                        value={groupBy}
                        onChange={(next) => setGroupBy(next as GroupBy)}
                    />
                }
            />

            {!isLoading && !data?.fellBackToLastRun && (data?.breakdown.length ?? 0) === 0 && (
                <Text fz={13} c={neutralColors.textSecondary}>
                    Nothing was recorded as produced on this day. A run keyed later for this
                    date will appear here — the day is the manufacturer&apos;s claim about when
                    it happened, not when it was typed.
                </Text>
            )}
        </div>
    );
};

export default ProductionMonitorContainer;
